import { useCallback, useState } from 'react';
import { useMount, useDestroy } from '.';
import axios, { AxiosRequestConfig, Method, CancelTokenSource } from 'axios';
import JSONBIG from 'json-bigint'
import { message } from 'antd';

export type UseFetchOptions<P> = Omit<AxiosRequestConfig, 'url'> & {
  method?: Method;
  params?: P;
  data?: P;
};

export type UseFetchConfigs<S, P> = {
  once?: boolean;
  manual: boolean;
  onResponse?: (data: S, params: P) => void;
  onFail?: (error: Error) => void;
}

export type UseFetchResult<S, P> = {
  error: Error | undefined;
  loading: boolean;
  data: S | undefined;
  run: (opts: UseFetchOptions<P>) => void;
}


function useFetch<T, P = Record<string, unknown>, S = CustomResponse<T>>(
  url: string = '',
  options: UseFetchOptions<P> = {},
  configs: UseFetchConfigs<S, P> = { manual: false }
): UseFetchResult<S, P> {
  const [data, setData] = useState<S>();
  const [error, setError] = useState<Error>();
  const [loading, setLoading] = useState<boolean>(false);

  const run = (opts: UseFetchOptions<P> = options) => configs.manual && request(opts);

  const request = useCallback((opts: typeof options) => {
    const rOptions = Object.assign(opts, options)
    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();
    setLoading(true);
    axios({
      url,
      ...rOptions,
      cancelToken: source.token,
      transformResponse: [function (data) {
        if (!(data instanceof Blob)) {
          const json = JSONBIG({
            storeAsString: true
          });
          const res = json.parse(data);

          if(res.msg == "401") {
            const path = typeof window !== "undefined" ? window.location.pathname : "";
            if (!path.startsWith("/doc/")) {
              window.top?.location.assign("/sign/login");
            }
          }

          return res;
        }
        return data;
      }]
    }).then(({ data }) => {
      setData(data);

      configs.manual
        && configs?.onResponse?.(data, rOptions?.params ?? rOptions?.data ?? {});
    }).catch(error => {
        if (!axios.isCancel(error)) {
          setError(error);
          configs?.onFail?.(error);
          // if(error.response?.data && error.response.data.type == "application/json") {
          //   message.error('导出失败,数据为空');
          // }else {
          //   setError(error);
          //   configs?.onFail?.(error);
          // }
        }
      })
      .finally(() => {
        setLoading(false);
      });
    return source;
  }, [url, options, configs]);

  let source: CancelTokenSource;

  useMount(() => {
    if (!configs.manual || configs.once === true)
      source = request(options);
  });

  useDestroy(() => {
    if (!configs.manual) source.cancel();
  });

  return { error, loading, data, run };
}

export default useFetch;